import { useState, useCallback } from 'react';
import AssociationService from '../services/associationService';

/**
 * Hook customizado para gerenciar verificações de associações de leads e oportunidades
 * @param {string} conversationInAttendanceId - ID da conversa em atendimento
 * @returns {Object} Dados e funções relacionadas às associações
 */
export const useConversationAssociations = (conversationInAttendanceId) => {
  const [associations, setAssociations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLeadAssociations, setHasLeadAssociations] = useState(false);
  const [hasOpportunityAssociations, setHasOpportunityAssociations] =
    useState(false);

  /**
   * Verifica se um lead específico já está associado
   * @param {number} leadId - ID do lead
   * @returns {boolean}
   */
  const isLeadAssociated = useCallback(
    (leadId) => {
      if (!associations?.data?.leadStatus) return false;
      const leadStatus = associations.data.leadStatus.find(
        (status) => status.leadId === leadId
      );
      return leadStatus?.isAssociated || false;
    },
    [associations]
  );

  /**
   * Verifica se uma oportunidade específica já está associada
   * @param {number} opportunityId - ID da oportunidade
   * @returns {boolean}
   */
  const isOpportunityAssociated = useCallback(
    (opportunityId) => {
      if (!associations?.data?.opportunityStatus) return false;
      const opportunityStatus = associations.data.opportunityStatus.find(
        (status) => status.opportunityId === opportunityId
      );
      return opportunityStatus?.isAssociated || false;
    },
    [associations]
  );

  /**
   * Obtém as associações existentes
   * @returns {Array}
   */
  const getExistingAssociations = useCallback(() => {
    return associations?.data?.existingAssociations || [];
  }, [associations]);

  /**
   * Obtém as associações de leads
   * @returns {Array}
   */
  const getLeadAssociations = useCallback(() => {
    const existing = getExistingAssociations();
    return existing.filter(
      (assoc) =>
        (assoc.associationType === 'lead' ||
          assoc.associationType === 'both') &&
        assoc.isActive
    );
  }, [getExistingAssociations]);

  /**
   * Obtém as associações de oportunidades
   * @returns {Array}
   */
  const getOpportunityAssociations = useCallback(() => {
    const existing = getExistingAssociations();
    return existing.filter(
      (assoc) =>
        (assoc.associationType === 'opportunity' ||
          assoc.associationType === 'both') &&
        assoc.isActive
    );
  }, [getExistingAssociations]);

  /**
   * Carrega as associações da conversa
   * @param {Array} leadIds - IDs dos leads (opcional)
   * @param {Array} opportunityIds - IDs das oportunidades (opcional)
   */
  const checkAssociations = useCallback(
    async (leadIds = [], opportunityIds = []) => {
      if (!conversationInAttendanceId) {
        setError('ID da conversa não fornecido');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await AssociationService.checkConversationAssociations(
          conversationInAttendanceId,
          leadIds,
          opportunityIds
        );

        setAssociations(response);

        // Atualiza estados auxiliares
        const hasLeads =
          response?.data?.existingAssociations?.some(
            (assoc) =>
              (assoc.associationType === 'lead' ||
                assoc.associationType === 'both') &&
              assoc.isActive
          ) || false;

        const hasOpportunities =
          response?.data?.existingAssociations?.some(
            (assoc) =>
              (assoc.associationType === 'opportunity' ||
                assoc.associationType === 'both') &&
              assoc.isActive
          ) || false;

        setHasLeadAssociations(hasLeads);
        setHasOpportunityAssociations(hasOpportunities);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Erro ao verificar associações';
        setError(errorMessage);
        setAssociations(null);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationInAttendanceId]
  );

  /**
   * Limpa os dados de associações
   */
  const clearAssociations = useCallback(() => {
    setAssociations(null);
    setError(null);
    setHasLeadAssociations(false);
    setHasOpportunityAssociations(false);
  }, []);

  /**
   * Recarrega as associações
   */
  const refreshAssociations = useCallback(async () => {
    if (associations) {
      const leadIds =
        associations.data?.leadStatus?.map((status) => status.leadId) || [];
      const opportunityIds =
        associations.data?.opportunityStatus?.map(
          (status) => status.opportunityId
        ) || [];
      await checkAssociations(leadIds, opportunityIds);
    }
  }, [associations, checkAssociations]);

  return {
    // Estados
    associations,
    isLoading,
    error,
    hasLeadAssociations,
    hasOpportunityAssociations,

    // Funções
    checkAssociations,
    clearAssociations,
    refreshAssociations,
    isLeadAssociated,
    isOpportunityAssociated,
    getExistingAssociations,
    getLeadAssociations,
    getOpportunityAssociations
  };
};
